import { Component } from "@theme/component";

const CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const MIN_RATING = 4;
const inflight = new Map();

const DUTCH_MONTHS = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

function formatDutchDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return `${date.getDate()} ${DUTCH_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

function renderStars(rating) {
  const filled = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  return "★".repeat(filled) + "☆".repeat(5 - filled);
}

async function fetchReviews(id, publicCode, limit) {
  const cacheKey = `wwk-reviews-${id}-${limit}`;

  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const { data, expires } = JSON.parse(cached);
      if (expires > Date.now()) return data;
    }
  } catch {}

  if (inflight.has(cacheKey)) return inflight.get(cacheKey);

  const url = `https://dashboard.webwinkelkeur.nl/api/1.0/ratings.json?id=${encodeURIComponent(
    id,
  )}&public_code=${encodeURIComponent(publicCode)}&limit=${encodeURIComponent(limit)}`;

  const promise = fetch(url)
    .then((response) => response.json())
    .then((json) => {
      if (json?.status !== "success" || !Array.isArray(json.ratings)) {
        console.warn(
          `[webwinkelkeur-reviews] API did not return reviews (id=${id}). Response:`,
          json?.message || json,
        );
        return null;
      }
      const data = { ratings: json.ratings, total: json.total };
      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ data, expires: Date.now() + CACHE_TTL_MS }),
        );
      } catch {}
      return data;
    })
    .catch((error) => {
      console.warn(`[webwinkelkeur-reviews] Request failed (id=${id}):`, error);
      return null;
    })
    .finally(() => {
      inflight.delete(cacheKey);
    });

  inflight.set(cacheKey, promise);
  return promise;
}

async function fetchSummary(id, publicCode) {
  const cacheKey = `wwk-summary-${id}`;

  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const { data, expires } = JSON.parse(cached);
      if (expires > Date.now()) return data;
    }
  } catch {}

  if (inflight.has(cacheKey)) return inflight.get(cacheKey);

  const url = `https://dashboard.webwinkelkeur.nl/api/1.0/ratings_summary.json?id=${encodeURIComponent(
    id,
  )}&public_code=${encodeURIComponent(publicCode)}`;

  const promise = fetch(url)
    .then((response) => response.json())
    .then((json) => {
      if (json?.status !== "success" || !json.data) {
        console.warn(
          `[webwinkelkeur-badge] API did not return a summary (id=${id}). Response:`,
          json?.message || json,
        );
        return null;
      }
      const data = {
        amount: json.data.amount,
        ratingAverage: json.data.rating_average,
      };
      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ data, expires: Date.now() + CACHE_TTL_MS }),
        );
      } catch {}
      return data;
    })
    .catch((error) => {
      console.warn(`[webwinkelkeur-badge] Request failed (id=${id}):`, error);
      return null;
    })
    .finally(() => {
      inflight.delete(cacheKey);
    });

  inflight.set(cacheKey, promise);
  return promise;
}

class WebwinkelkeurReviews extends Component {
  async connectedCallback() {
    super.connectedCallback();

    const { webshopId, publicCode } = this.dataset;
    if (!webshopId || !publicCode) return;

    const filterWithComment = this.dataset.requireComment === "true";
    // Reviews below MIN_RATING are always filtered out, so fetch a larger
    // batch (API max 100) to keep enough reviews left over to fill every card.
    const data = await fetchReviews(webshopId, publicCode, 100);
    if (!data?.ratings) return;

    const cards = this.querySelectorAll('[data-review-card]');

    let reviews = data.ratings.filter((r) => Number(r.rating) >= MIN_RATING);
    if (filterWithComment) {
      reviews = reviews.filter((r) => r.comment && r.comment.trim() !== "");
    }

    cards.forEach((card, index) => {
      const review = reviews[index];
      if (!review) {
        card.hidden = true;
        return;
      }
      card.hidden = false;

      const nameEl = card.querySelector('[data-review-name]');
      const starsEl = card.querySelector('[data-review-stars]');
      const commentEl = card.querySelector('[data-review-comment]');
      const dateEl = card.querySelector('[data-review-date]');

      if (nameEl) nameEl.textContent = review.name || "";
      if (starsEl) starsEl.textContent = renderStars(review.rating);
      if (commentEl) {
        const text = (review.comment || "").trim();
        commentEl.textContent = text;
        commentEl.hidden = text === "";
      }
      if (dateEl) dateEl.textContent = formatDutchDate(review.date || review.created);
    });
  }
}

class WebwinkelkeurBadge extends Component {
  async connectedCallback() {
    super.connectedCallback();

    const { webshopId, publicCode } = this.dataset;
    if (!webshopId || !publicCode) return;

    const data = await fetchSummary(webshopId, publicCode);
    if (!data) return;

    const scoreEl = this.querySelector("[data-wwk-score]");
    const countEl = this.querySelector("[data-wwk-count]");

    const score = Number(data.ratingAverage);
    if (scoreEl && Number.isFinite(score) && score > 0) {
      scoreEl.textContent = score.toFixed(1).replace(".", ",");
    }
    const amount = Number(data.amount);
    if (countEl && Number.isFinite(amount) && amount > 0) {
      countEl.textContent = String(amount);
    }
  }
}

if (!customElements.get("webwinkelkeur-reviews")) {
  customElements.define("webwinkelkeur-reviews", WebwinkelkeurReviews);
}

if (!customElements.get("webwinkelkeur-badge")) {
  customElements.define("webwinkelkeur-badge", WebwinkelkeurBadge);
}
